import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { z } from 'zod'; // TODO Standard Schema
import type { Result } from '../types/result';
import { retry } from '../utils/retry';

export const createAuthentication = <TSession extends object, TCredentials extends object>({
    sessionSchema,
    credentialsSchema,
    sessionQueryKey,
    getAccessTokenExpirationDate,
    loginCall,
    logoutCall,
    refreshCall,
}: {
    sessionSchema: z.Schema<TSession>;
    credentialsSchema: z.Schema<TCredentials>;
    sessionQueryKey: QueryKey;
    getAccessTokenExpirationDate: (session: NoInfer<TSession>) => Date;
    loginCall: (data: NoInfer<TCredentials>) => Promise<Result<NoInfer<TSession>>>;
    logoutCall: () => Promise<Result<void>>;
    refreshCall: () => Promise<Result<NoInfer<TSession>>>;
}) => {
    const useLogin = () => {
        const queryClient = useQueryClient();

        return useCallback(
            async (data: TCredentials): Promise<Result<void>> => {
                const inputValidation = credentialsSchema.safeParse(data);
                if (!inputValidation.success) {
                    return inputValidation;
                }

                const loginResult = await loginCall(data);
                if (!loginResult.success) {
                    return loginResult;
                }

                const outputValidation = sessionSchema.safeParse(loginResult.data);
                if (!outputValidation.success) {
                    throw outputValidation.error;
                }

                queryClient.setQueryData(sessionQueryKey, outputValidation.data);
                return { success: true, data: undefined };
            },
            [credentialsSchema.safeParse, queryClient.setQueryData, sessionSchema.safeParse, loginCall, sessionQueryKey]
        );
    };

    const useLogout = () => {
        const queryClient = useQueryClient();

        return useCallback(async (): Promise<Result<void>> => {
            const logoutResult = await logoutCall();
            if (!logoutResult.success) {
                return logoutResult;
            }

            queryClient.setQueryData(sessionQueryKey, null);
            return { success: true, data: undefined };
        }, [logoutCall, sessionQueryKey, queryClient.setQueryData]);
    };

    const useSession = ():
        | { status: 'loading' | 'unauthenticated'; data?: never }
        | { status: 'authenticated'; data: TSession } => {
        const queryClient = useQueryClient();

        const { status, data } = useQuery({
            queryKey: sessionQueryKey,
            queryFn: async () => {
                const refreshResult = await (async () => {
                    const previousData = sessionSchema.safeParse(queryClient.getQueryData(sessionQueryKey));
                    if (!previousData.success) {
                        return await retry(refreshCall, 1);
                    }

                    const retries = 2;
                    const remainingMilliseconds =
                        getAccessTokenExpirationDate(previousData.data).getTime() - Date.now();

                    return await retry(refreshCall, retries, remainingMilliseconds / (retries + 1));
                })();
                if (!refreshResult.success) {
                    await logoutCall();
                    throw refreshResult.error;
                }
                return sessionSchema.parse(refreshResult.data);
            },
            enabled: true,
            staleTime: Number.POSITIVE_INFINITY,
            gcTime: Number.POSITIVE_INFINITY,
            retry: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: true,
            refetchInterval: ({ state: { data } }) => {
                if (!data) {
                    return false;
                }
                const remainingMilliseconds = getAccessTokenExpirationDate(data).getTime() - Date.now();

                return (3 / 4) * remainingMilliseconds;
            },
        });

        switch (status) {
            case 'pending': {
                return { status: 'loading' };
            }
            case 'error': {
                return { status: 'unauthenticated' };
            }
            case 'success': {
                const result = sessionSchema.safeParse(data);
                if (!result.success) {
                    return { status: 'unauthenticated' };
                }
                return { status: 'authenticated', data: result.data };
            }
            default: {
                return status satisfies never;
            }
        }
    };

    return { useLogin, useLogout, useSession };
};
